import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, Star, Building, CheckCircle, Phone, Calendar, User, MessageSquare, Clock, Award, Eye, Heart, Share2, Edit, AlertTriangle, Target } from 'lucide-react';

interface OneToOneCall {
  id: string;
  promoterName: string;
  promoterCompany: string;
  promoterAvatar: string;
  submissionDate?: string;
  activatedDate?: string;
  status: string;
  frequency: string;
  product: string;
  sector?: string;
  notes?: string;
  totalCalls?: number;
  successRate?: number;
  rating?: number;
  review?: string;
  availability: {
    days: string[];
    timeSlots: string[];
    timezone: string;
  };
  type?: string;
}

const OneToOneCallDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  const call: OneToOneCall = {
    id: id || '1',
    promoterName: 'Sophie Durand',
    promoterCompany: 'Innovation Lab',
    promoterAvatar: 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=150',
    submissionDate: '2024-02-01',
    status: 'pending_review',
    frequency: 'monthly',
    product: 'Clareefai Enterprise',
    sector: 'Innovation',
    notes: 'Excellent promoter, very detailed availability',
    availability: {
      days: ['Monday', 'Wednesday', 'Friday'],
      timeSlots: ['10:00-12:00', '14:00-16:00'],
      timezone: 'CET (UTC+1)'
    },
    type: 'promoter_submission'
  };

  const handleClose = () => {
    navigate('/1to1-calls');
  };

  const handleApprove = async () => {
    if (!call) return;

    setIsApproving(true);

    await new Promise(resolve => setTimeout(resolve, 2000));

    console.log('Approving call');

    setIsApproving(false);
  };

  const handleReject = async () => {
    if (!call) return;

    setIsRejecting(true);

    await new Promise(resolve => setTimeout(resolve, 1500));

    console.log('Rejecting call');

    setIsRejecting(false);
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      pending_review: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      editing_required: 'bg-orange-100 text-orange-800',
      rejected: 'bg-red-100 text-red-800',
      activated: 'bg-[#4339A0] text-white'
    };

    const labelKeys: Record<string, string> = {
      pending_review: 'oneToOneCalls.status.pendingReview',
      approved: 'oneToOneCalls.status.approved',
      editing_required: 'oneToOneCalls.status.editingRequired',
      rejected: 'oneToOneCalls.status.rejected',
      activated: 'oneToOneCalls.status.activated'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${badges[status as keyof typeof badges] || 'bg-gray-100 text-gray-800'}`}>
        {labelKeys[status] ? t(labelKeys[status]) : status}
      </span>
    );
  };

  const getFrequencyBadge = (frequency: string) => {
    const badges = {
      weekly: 'bg-green-100 text-green-800',
      monthly: 'bg-blue-100 text-blue-800',
      'bi-monthly': 'bg-purple-100 text-purple-800',
      quarterly: 'bg-orange-100 text-orange-800'
    };

    const labelKeys: Record<string, string> = {
      weekly: 'oneToOneCalls.frequency.weekly',
      monthly: 'oneToOneCalls.frequency.monthly',
      'bi-monthly': 'oneToOneCalls.frequency.biMonthly',
      quarterly: 'oneToOneCalls.frequency.quarterly'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badges[frequency as keyof typeof badges]}`}>
        {labelKeys[frequency] ? t(labelKeys[frequency]) : frequency}
      </span>
    );
  };

  const isPromoterSubmission = call?.type === 'promoter_submission' ||
                              ['pending_review', 'approved', 'editing_required', 'rejected'].includes(call?.status || '');

  if (!call) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isPromoterSubmission ? t('oneToOneCalls.callDetails.submissionTitle') : t('oneToOneCalls.callDetails.detailsTitle')}
          </h1>
          <p className="text-gray-600 mt-1">
            {isPromoterSubmission ? t('oneToOneCalls.callDetails.submissionSubtitle') : t('oneToOneCalls.callDetails.detailsSubtitle')}
          </p>
        </div>
        <button
          onClick={handleClose}
          className="flex items-center space-x-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <X className="w-4 h-4" />
          <span>{t('oneToOneCalls.buttons.back')}</span>
        </button>
      </div>

      <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('oneToOneCalls.callDetails.promoterInfo')}</h3>
              <div className="flex items-center space-x-4">
                <img
                  src={call.promoterAvatar}
                  alt={call.promoterName}
                  className="w-16 h-16 rounded-full object-cover border-4 border-white shadow-lg"
                />
                <div className="flex-1">
                  <h4 className="text-xl font-bold text-gray-900">{call.promoterName}</h4>
                  <p className="text-gray-600 flex items-center mt-1">
                    <Building className="w-4 h-4 mr-1" />
                    {call.promoterCompany}
                  </p>
                  <div className="flex items-center space-x-4 mt-2">
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar className="w-4 h-4 mr-1" />
                      {call.submissionDate && new Date(call.submissionDate).toLocaleDateString('en-US')}
                    </div>
                    {call.sector && (
                      <div className="flex items-center text-sm text-gray-500">
                        <Target className="w-4 h-4 mr-1" />
                        {call.sector}
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  {getStatusBadge(call.status)}
                  <div className="mt-2">
                    {getFrequencyBadge(call.frequency)}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                <Clock className="w-5 h-5 mr-2 text-blue-600" />
                {t('oneToOneCalls.callDetails.availabilityDetails')}
              </h4>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="font-medium text-gray-600">{t('oneToOneCalls.callDetails.frequencyLabel')}</span>
                    <p className="text-gray-900">{call.frequency}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">{t('oneToOneCalls.callDetails.timezoneLabel')}</span>
                    <p className="text-gray-900">{call.availability.timezone}</p>
                  </div>
                </div>

                <div>
                  <span className="font-medium text-gray-600">{t('oneToOneCalls.callDetails.availableDays')}</span>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {call.availability.days.map((day, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
                      >
                        {day}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <span className="font-medium text-gray-600">{t('oneToOneCalls.callDetails.timeSlots')}</span>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {call.availability.timeSlots.map((slot, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium"
                      >
                        {slot}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {call.review && call.rating && (
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                  <Star className="w-5 h-5 mr-2 text-yellow-500" />
                  {t('oneToOneCalls.callDetails.customerReview')}
                </h4>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center mb-3">
                    <div className="flex items-center space-x-1">
                      {[...Array(call.rating)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                      ))}
                    </div>
                    <span className="ml-2 text-sm text-gray-600">({call.rating}/5)</span>
                  </div>
                  <p className="text-gray-800 italic">"{call.review}"</p>
                </div>
              </div>
            )}

            {call.totalCalls !== undefined && call.successRate !== undefined && (
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                  <Award className="w-5 h-5 mr-2 text-green-500" />
                  {t('oneToOneCalls.callDetails.performanceStats')}
                </h4>

                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <Phone className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-blue-900">{call.totalCalls}</div>
                    <div className="text-sm text-blue-700">{t('oneToOneCalls.callDetails.totalCalls')}</div>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-green-900">{call.successRate}%</div>
                    <div className="text-sm text-green-700">{t('oneToOneCalls.callDetails.successRate')}</div>
                  </div>
                </div>
              </div>
            )}

            {isPromoterSubmission && (
              <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('oneToOneCalls.callDetails.reviewActions')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {call.status === 'pending_review' && (
                    <>
                      <button
                        onClick={handleApprove}
                        disabled={isApproving}
                        className="flex items-center justify-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isApproving ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            <span>{t('oneToOneCalls.callDetails.approving')}</span>
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-5 h-5" />
                            <span>{t('oneToOneCalls.callDetails.approve')}</span>
                          </>
                        )}
                      </button>
                      <button
                        onClick={handleReject}
                        disabled={isRejecting}
                        className="flex items-center justify-center space-x-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isRejecting ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            <span>{t('oneToOneCalls.callDetails.rejecting')}</span>
                          </>
                        ) : (
                          <>
                            <AlertTriangle className="w-5 h-5" />
                            <span>{t('oneToOneCalls.callDetails.reject')}</span>
                          </>
                        )}
                      </button>
                    </>
                  )}

                  {call.status === 'approved' && (
                    <button className="flex items-center justify-center space-x-2 px-6 py-3 bg-[#4339A0] text-white rounded-lg hover:bg-[#3d2f8f] transition-colors">
                      <Phone className="w-5 h-5" />
                      <span>{t('oneToOneCalls.callDetails.activate')}</span>
                    </button>
                  )}

                  {['pending_review', 'approved'].includes(call.status) && (
                    <button className="flex items-center justify-center space-x-2 px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors">
                      <Edit className="w-5 h-5" />
                      <span>{t('oneToOneCalls.callDetails.requestEdit')}</span>
                    </button>
                  )}
                </div>

                {call.status === 'pending_review' && (
                  <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <Clock className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-yellow-900">{t('oneToOneCalls.callDetails.awaitingReview')}</h4>
                        <p className="text-sm text-yellow-800 mt-1">
                          {t('oneToOneCalls.callDetails.awaitingReviewMessage')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {call.status === 'approved' && (
                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-green-900">{t('oneToOneCalls.callDetails.approvedHeading')}</h4>
                        <p className="text-sm text-green-800 mt-1">
                          {t('oneToOneCalls.callDetails.approvedMessage')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {call.status === 'activated' && (
                  <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <Phone className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-purple-900">{t('oneToOneCalls.callDetails.currentlyActivated')}</h4>
                        <p className="text-sm text-purple-800 mt-1">
                          {t('oneToOneCalls.callDetails.currentlyActivatedMessage')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {call.notes && (
              <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('oneToOneCalls.callDetails.notes')}</h3>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <MessageSquare className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <p className="text-yellow-800">{call.notes}</p>
                  </div>
                </div>
              </div>
            )}
      </div>
    </div>
  );
};

export default OneToOneCallDetails;
